/**
 * Donde el socket se encuentra con la caché de queries.
 *
 * Un socket por sesión, montado una vez dentro de `AuthProvider` — la
 * colocación importa dos veces. Adentro, porque la llamada de ticket necesita
 * un cliente autenticado. Una vez, porque un socket abierto por el componente
 * que dibuja la campana es un socket que reconecta cada vez que ese componente
 * se remonta, y una campana en un encabezado se remonta con cada cambio de
 * layout.
 *
 * Lo que este componente hace con una trama es la parte que vale leer. Tiene
 * dos opciones, y usa ambas por razones distintas:
 *
 * - `setQueryData` para el badge, porque la trama lleva el conteo autoritativo
 *   y aplicarlo directo es instantáneo y gratis.
 * - `invalidateQueries` para la lista, porque la trama lleva una fila y la
 *   lista está paginada, filtrada y ordenada. Empalmar una fila en una página
 *   que el usuario quizá no está mirando es cómo una lista termina con 51
 *   ítems en una página de 50, o con una fila que desaparece en el siguiente
 *   refetch porque no cuadraba con el filtro. La invalidación deja que el
 *   servidor decida, y TanStack Query solo vuelve a pedir lo que está montado.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { env } from '@/config/env';
import { useAuth } from '@/features/auth/hooks/useAuth';

import { NotificationSocketContext } from './notificationSocketContext';
import { NotificationSocket } from './notificationSocket';
import { notificationKeys } from './useNotifications';
import { notificationsApi } from './notificationsApi';
import { MESSAGE_BADGE, MESSAGE_CREATED, MESSAGE_READY, type SocketStatus } from './types';

function socketUrl(): string {
  // Derivado del origen de la API, no de `window.location`: la SPA y la API
  // están en hosts distintos en todo entorno por encima de local. En local
  // `VITE_API_URL` está vacío y Vite proxya /ws, así que el propio origen sirve.
  const base = env.VITE_API_URL || window.location.origin;
  return `${base.replace(/^http/, 'ws')}/ws/notifications/`;
}

export function NotificationSocketProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SocketStatus>('closed');

  // El query client es estable, pero leerlo por un ref lo mantiene fuera de las
  // dependencias del effect — una dependencia que cambia de identidad derriba y
  // reabre el socket, que es una reconexión que nadie pidió.
  const clientRef = useRef(queryClient);
  clientRef.current = queryClient;

  useEffect(() => {
    if (!isAuthenticated) return;
    // In local the `runserver` is WSGI and serves no WebSockets, so an always-on
    // socket 404s and retries in a loop. The flag opts in only when the dev runs
    // uvicorn + redis; production deploys set it to true.
    if (!env.VITE_ENABLE_WEBSOCKET) return;

    const socket = new NotificationSocket({
      url: socketUrl(),
      getTicket: async () => (await notificationsApi.wsTicket()).ticket,
      onStatus: setStatus,

      onResync: () => {
        void clientRef.current.invalidateQueries({ queryKey: notificationKeys.all });
      },

      onFrame: (frame) => {
        switch (frame.type) {
          case MESSAGE_READY:
          case MESSAGE_BADGE:
            clientRef.current.setQueryData(notificationKeys.unread(), frame.payload.unread);
            break;

          case MESSAGE_CREATED:
            clientRef.current.setQueryData(notificationKeys.unread(), frame.payload.unread);
            void clientRef.current.invalidateQueries({ queryKey: notificationKeys.all });
            break;

          default:
            // `connection.closing` y cualquier cosa que un servidor más nuevo
            // envíe. Ignorado a propósito: el switch nunca debe lanzar, y una
            // trama que este build no entiende no es un error.
            break;
        }
      },
    });

    socket.start();
    return () => socket.stop();
  }, [isAuthenticated]);

  const value = useMemo(() => ({ status }), [status]);

  return (
    <NotificationSocketContext.Provider value={value}>
      {children}
    </NotificationSocketContext.Provider>
  );
}
